import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import moment from 'moment';
import 'moment/locale/ru';
import last from 'lodash/last';
import uniqBy from 'lodash/uniqBy';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import upperFirst from 'lodash/upperFirst';

import { setMobileMenuEnabled, setDocumentTitleBase } from '../../actions/actions';
import { exposeComponentToGlobalScope, inflect, isMobileSizeScreen } from '../../utils/utilsGlobal';
import services, { events } from '../../services/services';
import { getTaskByJiraId, searchJiraTasks } from '../../services/apiChat';
import { getIssues } from '../../services/apiIssues';
import { Dropdown, Tooltip } from '../../components';

import styles from './IssueList.scss';
import uiStyles from '../../styles/ui.scss';


export const DEFAULT_ISSUE_LIST_FILTERS = {
  sort: 'important',  // issue sorting; could also be 'latest' or 'longest'
  closed: false,      // if true, returns closed issues only
  linkedTo: null,     // ID of a linked task
};


const mapStateToProps = state => {
  return {
    currentCompany: state.currentCompany,
  };
};

const mapDispatchToProps = { setMobileMenuEnabled, setDocumentTitleBase };

@connect(mapStateToProps, mapDispatchToProps)
export class IssueList extends Component {

  state = {
    isLoading: false,
    isLoadingPrecedingIssues: false,
    issues: [],
    allIssuesLoaded: false,
    filterStatus: null,
    filterSort: null,
    filterLinkedToTaskTitle: '',
    filterLinkedToTaskProviderId: null,
    filterLinkedToTaskSearchQuery: '',
    filterLinkedToTaskResults: [],
    filterLinkedToTaskDefaultResults: [],
    searchQuery: '',
    isSearchOpen: false,
  };

  scrollBlock = React.createRef();
  searchInput = React.createRef();
  filterLinkedToTaskSearchInput = React.createRef();

  ISSUE_CHUNK_SIZE = 50;

  FILTER_SORT_ITEMS = [
    { title: 'Сначала острые', value: 'important' },
    { title: 'Сначала долгие', value: 'longest' },
    { title: 'Сначала последние', value: 'latest' },
  ];

  FILTER_LINKED_TO_TASK_DEFAULT_TITLE = 'Связь';

  debouncedSearchIssues = debounce(this.searchIssues.bind(this), 300);
  debouncedOnFilterLinkedToTaskSearch = debounce(this.onFilterLinkedToTaskSearch.bind(this), 300);

  componentDidMount(){
    const { filters, setDocumentTitleBase } = this.props;
    setDocumentTitleBase('Проблемы');

    this.setState({
      filterStatus: filters.closed ? 'closed' : 'open',
      filterSort: filters.sort || DEFAULT_ISSUE_LIST_FILTERS.sort,
      filterLinkedToTaskTitle: filters.linkedTo || this.FILTER_LINKED_TO_TASK_DEFAULT_TITLE,
      filterLinkedToTaskProviderId: filters.linkedTo || null,
    });
    services.userWatcher.on(events.USER_SET_SUCCESS, currentCompany => {
      this.initializeFilterLinkedToTask(currentCompany, filters.linkedTo);
      this.onIssueListChange(currentCompany, filters);
    });
    exposeComponentToGlobalScope(this);
  }

  componentDidUpdate(prevProps){
    const { filters, currentCompany } = this.props;
    const didFiltersChange = !isEqual(filters, prevProps.filters);
    if (didFiltersChange) {
      this.onIssueListChange(currentCompany, filters);
    }
  }

  onIssueListChange = (currentCompany, filters) => {
    const { searchQuery } = this.state;
    this.setState({
      isLoading: true,
      allIssuesLoaded: false,
    });
    return getIssues(currentCompany.id, searchQuery, this.normalizeFilters(filters), this.ISSUE_CHUNK_SIZE)
      .then(issues => {
        this.setState({
          issues,
          isLoading: false,
        });
        if (issues.length < this.ISSUE_CHUNK_SIZE) {
          this.setState({ allIssuesLoaded: true });
        }
      })
      .catch(() => this.setState({ isLoading: false }));
  };

  normalizeFilters = (rawFilters) => {
    const { sort, closed, linkedTo } = rawFilters;
    let normalizedFilters = { ...DEFAULT_ISSUE_LIST_FILTERS };

    if ( ['important', 'latest', 'longest'].includes((sort || '').toLowerCase()) ) {
      normalizedFilters.sort = (sort || '').toLowerCase();
    }
    if ( Boolean(closed) ) {
      normalizedFilters.closed = true;
    }
    if (typeof linkedTo === 'string' && linkedTo) {
      normalizedFilters.linkedTo = linkedTo;
    }
    return normalizedFilters;
  };

  initializeFilterLinkedToTask = (currentCompany, filterLinkedTo) => {
    searchJiraTasks(currentCompany.id, '', true).then(tasks => {
      const filterLinkedToTaskResults = this.getFilterLinkedToTaskItems(tasks);
      this.setState({
        filterLinkedToTaskResults,
        filterLinkedToTaskDefaultResults: filterLinkedToTaskResults,
      });
    });

    if (filterLinkedTo) {
      getTaskByJiraId(currentCompany.id, filterLinkedTo)
        .then(task => {
          if (task?.providerId) {
            this.setState({
              filterLinkedToTaskTitle: task.providerId + ': ' + task.title,
            });
          }
        })
        .catch((e) => {
          e.overrideErrorNotification({
            errorMessage: `Задача ${filterLinkedTo} не найдена или не связана ни с одной из проблем.`,
          });
          this.onFilterLinkedToTaskReset();
        });
    }
  };

  getFilterLinkedToTaskItems = (tasks) => {
    const items = tasks.map(item => {
      return {
        title: item.providerId + ': ' + item.title,
        value: item.providerId,
      };
    });
    return uniqBy(items, 'value');
  };

  onIssuesScroll = () => {
    const scrollBlock = this.scrollBlock.current;
    const maxScrollTop = scrollBlock.scrollHeight - scrollBlock.offsetHeight;
    if (scrollBlock.scrollTop >= maxScrollTop) {
      this.loadNextIssueChunk();
    }
  };

  loadNextIssueChunk = () => {

    const { filters, currentCompany } = this.props;
    const { searchQuery, issues, allIssuesLoaded, isLoadingPrecedingIssues } = this.state;
    const beforeCursor = last(issues)?.cursor;

    if (!allIssuesLoaded && beforeCursor && !isLoadingPrecedingIssues) {
      this.setState({ isLoadingPrecedingIssues: true });

      return getIssues(currentCompany.id, searchQuery, filters, this.ISSUE_CHUNK_SIZE, beforeCursor)
        .then(nextIssueChunk => {
          const updatedIssues = [ ...issues, ...nextIssueChunk ];
          this.setState({
            issues: updatedIssues,
            isLoadingPrecedingIssues: false,
          });
          if (nextIssueChunk.length < this.ISSUE_CHUNK_SIZE) {
            this.setState({ allIssuesLoaded: true });
          }
        })
        .catch(() => this.setState({ isLoadingPrecedingIssues: false }));
    }
    else {
      return Promise.resolve();
    }
  };

  onFilterStatusChange = (filterStatus) => {
    const { onFiltersChange } = this.props;

    if (filterStatus !== this.state.filterStatus) {
      const filterSort = filterStatus === 'closed' ? 'latest' : 'important';
      this.setState({ filterStatus, filterSort });

      onFiltersChange({
        closed: filterStatus === 'closed',
        sort: filterSort,
      });
    }
  };

  onFilterSortChange = (filterSort) => {
    const { onFiltersChange } = this.props;
    this.setState({ filterSort });
    onFiltersChange({ sort: filterSort });
  };

  toggleSearchInput = () => {
    const { currentCompany, filters } = this.props;
    const { isSearchOpen } = this.state;
    if (isSearchOpen) {
      this.setState({
        isSearchOpen: false,
        searchQuery: '',
      }, () => {
        this.onIssueListChange(currentCompany, filters);
      });
    }
    else {
      this.setState({ isSearchOpen: true });
      this.searchInput.current.focus();
    }
  };

  onSearchInputChange = (e) => {
    const searchQuery = e.target.value;
    this.setState({ searchQuery });
    this.debouncedSearchIssues(searchQuery);
  };

  searchIssues(searchQuery){
    const { filters, currentCompany } = this.props;
    this.setState({ isLoading: true, allIssuesLoaded: false });

    return getIssues(currentCompany.id, searchQuery, { sort: filters.sort }, this.ISSUE_CHUNK_SIZE)
      .then(issues => {
        this.setState({
          issues,
          isLoading: false,
        });
        if (issues.length < this.ISSUE_CHUNK_SIZE) {
          this.setState({ allIssuesLoaded: true });
        }
      })
      .catch(() => this.setState({ isLoading: false }));
  };

  onFilterLinkedToTaskSearch(query){
    const { currentCompany } = this.props;
    return searchJiraTasks(currentCompany.id, query, true).then(tasks => {
      this.setState({
        filterLinkedToTaskResults: this.getFilterLinkedToTaskItems(tasks),
      });
    });
  }

  onFilterLinkedToTaskReset = (e) => {
    const { onFiltersChange } = this.props;
    const { filterLinkedToTaskDefaultResults } = this.state;
    this.setState({
      filterLinkedToTaskTitle: this.FILTER_LINKED_TO_TASK_DEFAULT_TITLE,
      filterLinkedToTaskProviderId: null,
      filterLinkedToTaskSearchQuery: '',
      filterLinkedToTaskResults: filterLinkedToTaskDefaultResults,
    });
    onFiltersChange({ linkedTo: null });
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  onFilterLinkedToTaskChange = ({ title, value }) => {
    const { onFiltersChange } = this.props;
    this.setState({
      filterLinkedToTaskTitle: title,
      filterLinkedToTaskProviderId: value,
      filterLinkedToTaskSearchQuery: '',
    });
    onFiltersChange({ linkedTo: value });
  };

  onMobileIssueClick = (issue) => {
    if ( isMobileSizeScreen() ) {
      const { setMobileMenuEnabled, setDocumentTitleBase } = this.props;
      setMobileMenuEnabled(false);
      setDocumentTitleBase(issue.title);
    }
  };

  render(){
    const { className } = this.props;
    const {
      isLoading,
      isLoadingPrecedingIssues,
      issues,
      filterStatus,
      filterSort,
      filterLinkedToTaskTitle,
      filterLinkedToTaskProviderId,
      filterLinkedToTaskSearchQuery,
      filterLinkedToTaskResults,
      searchQuery,
      isSearchOpen,
    } = this.state;

    if (!filterStatus || !filterSort) {
      return null;
    }

    const filterSortItems = filterStatus === 'open'
      ? this.FILTER_SORT_ITEMS
      : [ ...this.FILTER_SORT_ITEMS ].reverse();

    return (
      <div className={cn(styles.container, className)}
        onScroll={this.onIssuesScroll}
        ref={this.scrollBlock}>

        <div className={styles.filters}>
          <Dropdown
            buttonAppearance={true}
            defaultValue={filterStatus}
            onChange={({ value }) => this.onFilterStatusChange(value)}
            items={[
              { title: 'Открытые', value: 'open' },
              { title: 'Решенные', value: 'closed' },
            ]}
            overrideClassNames={{
              container: styles.filtersItem,
              button: styles.filtersButton,
            }}>
            {filterStatus === 'open' ? 'Открытые' : 'Решенные'}
          </Dropdown>

          <Dropdown
            buttonAppearance={true}
            defaultValue={filterSort}
            onChange={({ value }) => this.onFilterSortChange(value)}
            items={filterSortItems}
            overrideClassNames={{
              container: styles.filtersItem,
              button: styles.filtersButton,
            }}>
            {this.FILTER_SORT_ITEMS.filter(item => item.value === filterSort)[0].title}
          </Dropdown>

          <Dropdown
            buttonAppearance={true}
            overrideClassNames={{
              container: styles.filtersItem,
              button: styles.filtersButton,
              buttonArrow: uiStyles.hidden,
              list: styles.linkedToTaskList,
              item: styles.linkedToTaskItem,
            }}
            dontKeepSelectedOption={true}
            items={filterLinkedToTaskResults}
            onChange={this.onFilterLinkedToTaskChange}
            renderListTop={() => (
              <input className={styles.linkedToTaskSearch}
                ref={this.filterLinkedToTaskSearchInput}
                type="text"
                placeholder="Поиск задачи в Jira"
                value={filterLinkedToTaskSearchQuery}
                onChange={(e) => {
                  this.setState({ filterLinkedToTaskSearchQuery: e.target.value });
                  this.debouncedOnFilterLinkedToTaskSearch(e.target.value);
                }}/>
            )}>
            <div onClick={() => requestAnimationFrame(() => { this.filterLinkedToTaskSearchInput.current?.focus?.() })}>
              <Tooltip className={styles.linkedToTaskTooltip}
                title={filterLinkedToTaskTitle}
                placement="bottom"
                overflownOnly={true}>
                <div className={styles.linkedToTaskButtonContent}>{filterLinkedToTaskTitle}</div>
              </Tooltip>
              {filterLinkedToTaskProviderId
                ? <i className={cn(styles.linkedToTaskReset, 'icon-close-medium')} onClick={this.onFilterLinkedToTaskReset}/>
                : <i className={cn(styles.linkedToTaskArrow, 'icon-chevron-down')}/>
              }
            </div>
          </Dropdown>

          <div className={cn({
            [styles.search]: true,
            [styles.searchOpen]: isSearchOpen,
          })}>
            <div className={styles.searchInputWrapper}>
              <input className={styles.searchInput}
                ref={this.searchInput}
                placeholder="Поиск проблемы"
                type="text"
                value={searchQuery}
                onChange={this.onSearchInputChange}
                onKeyUp={e => e.key === 'Escape' && this.toggleSearchInput()}
              />
            </div>
            <div className={styles.searchIconWrapper}>
              <i className={cn(styles.searchIcon, styles.searchIconGlass, 'icon-search')}
                onClick={this.toggleSearchInput}/>
              <i className={cn(styles.searchIcon, styles.searchIconClose, 'icon-close-medium')}
                onClick={this.toggleSearchInput}/>
            </div>
          </div>
        </div>

        <ul className={cn({
          [styles.list]: true,
          [styles.listLoading]: isLoading,
        })}>
          {issues.map(issue => (
            <li className={cn({
              [styles.listItem]: true,
              [styles.listItemClosed]: issue.isClosed,
            })} key={issue.id}>
              <h3 className={styles.listItemTitle}>
                <Link className={styles.listItemLink}
                  onClick={() => this.onMobileIssueClick(issue)}
                  to={`/issues/${issue.id}`}>
                  {issue.isClosed && (
                    <i className={cn(styles.listItemClosedTick, 'icon-system-case-closed')}/>
                  )}
                  {issue.title}
                </Link>
              </h3>
              <span className={styles.listItemDetails}>
                {inflect(issue.totalLinkedMessagesCount, ['сообщение', 'сообщения', 'сообщений'])}
                &nbsp;в {inflect(issue.totalLinkedRoomsCount, ['чате', 'чатах', 'чатах'])}
                <span className={styles.listItemDot}>&middot;</span>
                <Tooltip title={moment(issue.insertedAt).format('D MMMM YYYY, H:mm:ss')}>
                  <span>{upperFirst(moment(issue.insertedAt).fromNow())}</span>
                </Tooltip>
              </span>
            </li>
          ))}
        </ul>

        {!issues.length && !isLoading && (
          <div className={styles.notFoundMessage}>Проблемы не найдены</div>
        )}

        {isLoadingPrecedingIssues && (
          <span className={styles.loadingPrecedingIssuesLabel}>Загрузка...</span>
        )}
      </div>
    );
  }
}
